import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { X, Calendar, Clock, Award, Crown, Loader2, CheckCircle, Coins, Minus, Plus, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { priceStore } from '../utils/priceStore';
import { orderService } from '../utils/orderService';
import { supabase, getSupabaseUrl } from '../supabase/client';
import AlipayPaymentModal from './AlipayPaymentModal';

interface Paper {
  id: string;
  title: string;
  organization: string;
  category_type: string;
  level_text: string;
}

interface PackagePurchaseModalProps {
  paper: Paper;
  onClose: () => void;
  role?: 'student' | 'teacher';
}

const durationConfigs = [
  { key: 'month', label: '月包', months: 1, icon: Calendar },
  { key: 'quarter', label: '季包', months: 3, icon: Clock },
  { key: 'halfYear', label: '半年包', months: 6, icon: Award },
  { key: 'year', label: '年包', months: 12, icon: Crown },
];

const POINTS_PER_YUAN = 100;

export default function PackagePurchaseModal({ paper, onClose, role = 'student' }: PackagePurchaseModalProps) {
  const navigate = useNavigate();
  const [paying, setPaying] = useState(false);
  const [showAlipayModal, setShowAlipayModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<any>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [packagePrice, setPackagePrice] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDuration, setSelectedDuration] = useState<string>('');
  const [error, setError] = useState('');

  // U币抵扣相关状态
  const [totalPoints, setTotalPoints] = useState(0);
  const [usePoints, setUsePoints] = useState(true);
  const [pointsToUse, setPointsToUse] = useState(0);
  const [zeroPaymentProcessing, setZeroPaymentProcessing] = useState(false);

  const isTeacher = role === 'teacher';
  const themeColor = isTeacher ? 'purple' : 'blue';

  useEffect(() => {
    loadUserAndPrices();
  }, [paper, role]);

  const loadUserAndPrices = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single();
        setCurrentUser({ id: session.user.id, ...profile });

        // 查询U币余额
        try {
          const res = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ action: 'get_account', user_id: session.user.id, role }),
          });
          const result = await res.json();
          if (result.success && result.account) {
            setTotalPoints(result.account.total_points || 0);
          }
        } catch {}
      }

      // 根据角色加载价格
      if (isTeacher) {
        // 教师从 exam_levels 表获取价格
        const { data: levels } = await supabase
          .from('exam_levels')
          .select('id, level_name, price_month, price_quarter, price_half_year, price_year')
          .eq('level_name', paper.level_text)
          .eq('is_enabled', true);

        if (levels && levels.length > 0) {
          const level = levels[0];
          setPackagePrice({
            monthPrice: level.price_month || 0,
            quarterPrice: level.price_quarter || 0,
            halfYearPrice: level.price_half_year || 0,
            yearPrice: level.price_year || 0,
          });
        } else {
          setPackagePrice(null);
        }
      } else {
        // 学生从 priceStore 获取价格
        const price = await priceStore.findPriceByNames(paper.organization, paper.category_type, paper.level_text);
        setPackagePrice(price);
      }
    } catch (err) {
      setError('加载数据失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const getPrice = (key: string) => {
    if (!packagePrice) return 0;
    switch (key) {
      case 'month': return packagePrice.monthPrice;
      case 'quarter': return packagePrice.quarterPrice;
      case 'halfYear': return packagePrice.halfYearPrice;
      case 'year': return packagePrice.yearPrice;
      default: return 0;
    }
  };

  const maxDeductionYuan = useMemo(() => Math.floor(totalPoints / POINTS_PER_YUAN), [totalPoints]);

  const currentPrice = useMemo(() => getPrice(selectedDuration), [selectedDuration, packagePrice]);

  const deductionAmount = useMemo(() => {
    if (!usePoints || pointsToUse <= 0) return 0;
    // 使用整数运算避免浮点精度问题：先算分，再转元
    return Math.floor(pointsToUse / POINTS_PER_YUAN * 100) / 100;
  }, [usePoints, pointsToUse]);

  const actualPrice = useMemo(() => {
    // 使用整数运算（分）避免浮点精度问题
    const priceInCents = Math.round(currentPrice * 100);
    const deductionInCents = Math.round(deductionAmount * 100);
    const actualInCents = Math.max(0, priceInCents - deductionInCents);
    return actualInCents / 100;
  }, [currentPrice, deductionAmount]);

  const isZeroPayment = useMemo(() => actualPrice <= 0 && pointsToUse > 0, [actualPrice, pointsToUse]);

  const handleDurationSelect = (key: string) => {
    setSelectedDuration(key);
    const price = getPrice(key);
    const maxPointsForOrder = Math.min(totalPoints, Math.floor(price * POINTS_PER_YUAN));
    setPointsToUse(maxPointsForOrder);
    setUsePoints(totalPoints > 0);
  };

  const adjustPoints = (delta: number) => {
    const maxPointsForOrder = Math.min(totalPoints, Math.floor(currentPrice * POINTS_PER_YUAN));
    const newPoints = Math.max(0, Math.min(maxPointsForOrder, pointsToUse + delta));
    setPointsToUse(newPoints);
  };

  const handlePurchase = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!selectedDuration) return;

    const price = getPrice(selectedDuration);
    const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();

    // 统一使用 user_packages 表检查重复购买
    const { data: existingPackages } = await supabase
      .from('user_packages')
      .select('*')
      .eq('user_id', currentUser.id)
      .eq('is_active', true)
      .gte('expire_date', today)
      .eq('organization', paper.organization)
      .eq('category', paper.category_type)
      .eq('level_text', paper.level_text)
      .eq('role', role);

    if (existingPackages && existingPackages.length > 0) {
      setError('您已开通该刷题包，请勿重复购买');
      return;
    }

    setPaying(true);
    setError('');

    try {
      // 统一使用 createOrder，传入 role 参数
      const order = await orderService.createOrder({
        userId: currentUser.id,
        userName: currentUser.username || currentUser.phone || '未知用户',
        userPhone: currentUser.phone,
        orgName: paper.organization || '',
        catName: paper.category_type || '',
        levelText: paper.level_text || '',
        duration: selectedDuration as 'month' | 'quarter' | 'halfYear' | 'year',
        price: price,
        source: 'package',
        pointsDeducted: usePoints ? pointsToUse : 0,
        deductionAmount: usePoints ? deductionAmount : 0,
        role,
      });

      setCurrentOrder(order);

      if (isZeroPayment) {
        setZeroPaymentProcessing(true);
        try {
          const session = await supabase.auth.getSession();
          const res = await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.data.session?.access_token}`,
            },
            body: JSON.stringify({
              action: 'zero_payment',
              user_id: currentUser.id,
              order_id: order.id,
              order_no: order.orderNo,
              points_to_deduct: pointsToUse,
              deduction_amount: deductionAmount,
              role,
            }),
          });
          const result = await res.json();
          if (result.success) {
            setShowAlipayModal(false);
            window.dispatchEvent(new Event('package-purchased'));
            onClose();
            navigate(`/payment-result?status=success&orderNo=${order.orderNo}&pointsDeducted=${pointsToUse}&deductionAmount=${deductionAmount}`);
          } else {
            setError('U币支付失败：' + (result.error || '未知错误'));
          }
        } catch {
          setError('U币支付失败，请重试');
        } finally {
          setZeroPaymentProcessing(false);
          setPaying(false);
        }
      } else {
        setShowAlipayModal(true);
        setPaying(false);
      }
    } catch {
      setError('创建订单失败，请重试');
      setPaying(false);
    }
  };

  const handleAlipaySuccess = async () => {
    if (!currentOrder) return;

    // 幂等性检查：先查询订单状态，如果已经是 paid 则跳过通知
    const { data: orderCheck } = await supabase
      .from('orders')
      .select('status')
      .eq('id', currentOrder.id)
      .maybeSingle();

    const alreadyPaid = orderCheck?.status === 'paid';

    const durationKey = currentOrder.duration;
    const expireDate = new Date();
    const months = { month: 1, quarter: 3, halfYear: 6, year: 12 }[durationKey as 'month' | 'quarter' | 'halfYear' | 'year'] || 1;
    expireDate.setMonth(expireDate.getMonth() + months);

    // 统一写入 user_packages 表（使用 upsert 防止重复创建）
    const { error: packageError } = await supabase.from('user_packages').upsert({
      user_id: currentUser.id,
      role,
      organization: paper.organization,
      category: paper.category_type,
      level_text: paper.level_text,
      package_source: isTeacher ? 'teacher_purchase' : 'student_purchase',
      expire_date: `${expireDate.getFullYear()}-${String(expireDate.getMonth() + 1).padStart(2, '0')}-${String(expireDate.getDate()).padStart(2, '0')}`,
      is_active: true,
      duration_type: durationKey,
      order_id: currentOrder.id,
      price: currentOrder.price,
      actual_price: currentOrder.actualPrice || currentOrder.price,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'order_id' });

    if (packageError) {
      console.error('创建/更新套餐失败:', packageError);
    }

    // 更新订单状态
    await supabase.from('orders').update({
      organization: paper.organization,
      category: paper.category_type,
      level_text: paper.level_text,
      status: 'paid',
      paid_at: new Date().toISOString(),
    }).eq('id', currentOrder.id);

    // 教师U币返利：如果学员已绑定教师，教师获得消费金额50%的等价U币
    if (!isTeacher && !alreadyPaid) {
      try {
        // 查询学员是否绑定了教师
        const { data: teacherStudent } = await supabase
          .from('teacher_students')
          .select('teacher_id')
          .eq('student_id', currentUser.id)
          .maybeSingle();

        if (teacherStudent?.teacher_id) {
          // 计算返利U币：实付金额 * 50% * 100（1元=100U币）
          const actualAmount = currentOrder.actualPrice || currentOrder.price;
          const rebatePoints = Math.floor(actualAmount * 0.5 * 100);

          if (rebatePoints > 0) {
            const session = await supabase.auth.getSession();
            await fetch(`${getSupabaseUrl()}/functions/v1/point-service`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.data.session?.access_token}`,
              },
              body: JSON.stringify({
                action: 'add_points',
                user_id: teacherStudent.teacher_id,
                role: 'teacher',
                points: rebatePoints,
                point_type: 'activity_reward',
                source_type: 'teacher_rebate',
                source_id: currentOrder.id,
                source_detail: {
                  order_no: currentOrder.orderNo,
                  student_id: currentUser.id,
                  actual_amount: actualAmount,
                  rebate_rate: 0.5,
                },
                remark: `学员消费返利：订单${currentOrder.orderNo}，消费¥${actualAmount.toFixed(2)}，返利50%（${rebatePoints}U币）`,
              }),
            });
          }
        }
      } catch (e) {
        console.error('教师U币返利失败:', e);
      }
    }

    // 发送消息通知（仅当订单之前不是 paid 状态时才发送）
    if (!alreadyPaid) {
      try {
        const session = await supabase.auth.getSession();
        await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.data.session?.access_token}`,
          },
          body: JSON.stringify({
            action: 'messages_trigger_scene',
            sceneType: 'order_paid',
            userId: currentUser.id,
            variables: {
              order_no: currentOrder.orderNo,
              amount: String(currentOrder.actualPrice || currentOrder.price),
            },
          }),
        });
      } catch {}
    }

    setShowAlipayModal(false);
    window.dispatchEvent(new Event('package-purchased'));
    onClose();
    navigate(`/payment-result?status=success&orderNo=${currentOrder.orderNo}&pointsDeducted=${currentOrder.pointsDeducted || 0}&deductionAmount=${currentOrder.deductionAmount || 0}`);
  };

  const handleAlipayFail = () => {
    setShowAlipayModal(false);
    navigate(`/payment-result?status=fail`);
  };

  const btnClass = isTeacher
    ? 'bg-purple-600 hover:bg-purple-700'
    : 'bg-blue-600 hover:bg-blue-700';
  const textClass = isTeacher ? 'text-purple-600' : 'text-blue-600';
  const borderClass = isTeacher ? 'border-purple-500 bg-purple-50' : 'border-blue-500 bg-blue-50';
  const hoverBorderClass = isTeacher ? 'hover:border-purple-300' : 'hover:border-blue-300';
  const iconBgClass = isTeacher ? 'bg-purple-100' : 'bg-blue-100';

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl p-6 w-[420px]">
          <div className="flex items-center justify-center py-12">
            <Loader2 className={`w-8 h-8 ${textClass} animate-spin`} />
            <span className="ml-2 text-gray-600">加载中...</span>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl p-6 w-[420px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">购买刷题包</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5 text-gray-600" /></button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {!packagePrice ? (
          <div className="text-center py-8 text-gray-400">
            <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>该级别暂无定价配置</p>
          </div>
        ) : (
          <div className="space-y-3">
            {/* 套餐信息 */}
            <p className="text-sm text-gray-500 mb-2">
              {paper.organization} · {paper.category_type} · {paper.level_text}
            </p>

            {/* 套餐选择 */}
            {durationConfigs.map(config => {
              const price = getPrice(config.key);
              const isSelected = selectedDuration === config.key;
              return (
                <button
                  key={config.key}
                  onClick={() => handleDurationSelect(config.key)}
                  className={`w-full p-3 border-2 rounded-xl flex items-center justify-between transition-all hover:shadow-md ${
                    isSelected ? borderClass : `border-gray-200 ${hoverBorderClass}`
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${iconBgClass} rounded-lg flex items-center justify-center`}>
                      <config.icon className={`w-5 h-5 ${textClass}`} />
                    </div>
                    <div className="text-left">
                      <span className="font-medium text-gray-800">{config.label}</span>
                      <p className="text-xs text-gray-500">{config.months}个月有效期</p>
                    </div>
                  </div>
                  <span className={`text-xl font-bold ${textClass}`}>¥{price}</span>
                </button>
              );
            })}

            {/* U币抵扣区域 */}
            {selectedDuration && totalPoints > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-orange-200 bg-orange-50 rounded-lg p-3 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Coins className="w-4 h-4 text-orange-500" />
                    <span className="text-sm font-medium text-orange-700">U币抵扣</span>
                  </div>
                  <button
                    onClick={() => {
                      setUsePoints(!usePoints);
                      if (!usePoints) {
                        const maxPointsForOrder = Math.min(totalPoints, Math.floor(currentPrice * POINTS_PER_YUAN));
                        setPointsToUse(maxPointsForOrder);
                      } else {
                        setPointsToUse(0);
                      }
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      usePoints ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {usePoints ? '已开启' : '未开启'}
                  </button>
                </div>

                {usePoints && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>可用U币: {totalPoints}分 (≈¥{maxDeductionYuan})</span>
                      <span>使用: {pointsToUse}分 (≈¥{pointsToUse / POINTS_PER_YUAN})</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => adjustPoints(-100)}
                        className="w-8 h-8 flex items-center justify-center bg-white border rounded hover:bg-gray-50"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <input
                        type="number"
                        value={pointsToUse}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          const maxPointsForOrder = Math.min(totalPoints, Math.floor(currentPrice * POINTS_PER_YUAN));
                          setPointsToUse(Math.max(0, Math.min(maxPointsForOrder, val)));
                        }}
                        className="flex-1 text-center text-sm border rounded px-2 py-1"
                        step={100}
                        min={0}
                        max={Math.min(totalPoints, Math.floor(currentPrice * POINTS_PER_YUAN))}
                      />
                      <button
                        onClick={() => adjustPoints(100)}
                        className="w-8 h-8 flex items-center justify-center bg-white border rounded hover:bg-gray-50"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => {
                          const maxPointsForOrder = Math.min(totalPoints, Math.floor(currentPrice * POINTS_PER_YUAN));
                          setPointsToUse(maxPointsForOrder);
                        }}
                        className="px-2 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600"
                      >
                        全额
                      </button>
                    </div>
                  </div>
                )}

                {/* 价格明细 */}
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>原价</span>
                    <span>¥{currentPrice}</span>
                  </div>
                  {usePoints && pointsToUse > 0 && (
                  <div className="flex justify-between text-orange-600">
                    <span>U币抵扣</span>
                    <span>-¥{deductionAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold border-t pt-1">
                  <span>实付金额</span>
                  <span className={isZeroPayment ? 'text-green-600' : textClass}>
                    {isZeroPayment ? '¥0 (U币全额抵扣)' : `¥${actualPrice.toFixed(2)}`}
                  </span>
                </div>
                </div>
              </motion.div>
            )}

            {/* 购买按钮 */}
            {selectedDuration && (
              <button
                onClick={handlePurchase}
                disabled={paying || zeroPaymentProcessing}
                className={`w-full py-3 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2 transition-colors ${btnClass}`}
              >
                {zeroPaymentProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    U币支付中...
                  </>
                ) : paying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    创建订单中...
                  </>
                ) : isZeroPayment ? (
                  'U币全额支付'
                ) : (
                  `支付 ¥${actualPrice.toFixed(2)}`
                )}
              </button>
            )}
          </div>
        )}
      </motion.div>

      {currentOrder && !isZeroPayment && (
        <AlipayPaymentModal
          isOpen={showAlipayModal}
          onClose={() => setShowAlipayModal(false)}
          orderId={currentOrder?.id || ''}
          orderNo={currentOrder?.orderNo || ''}
          amount={currentOrder?.actualPrice || currentOrder?.price || 0}
          subject={`${paper.organization} ${paper.category_type} ${paper.level_text}`}
          onSuccess={handleAlipaySuccess}
          onFail={handleAlipayFail}
        />
      )}
    </div>
  );
}
